import { AdminIntroductions } from '@platform/client';
import { Icon, PillBadge, PillBadgeColors } from '@platform/components';
import { keepPreviousData } from '@tanstack/react-query';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { AdminIntroduction } from 'lib/client/src/platformapi.schemas';
import { useMemo, useState, useContext } from 'react';
import FlexibleLink from '../../../util/FlexibleLink';
import IntroductionOutcomeModal from './IntroductionOutcomeModal';
import { AdminTable, AdminTableContext } from '../../AdminTable';
import { AdminListPageWrapper } from '../../AdminListPage';

const columnHelper = createColumnHelper<AdminIntroduction>();

export const IntroductionsAdmin = () => {
  return (
    <AdminListPageWrapper title={'Introductions'}>
      <IntroductionsAdminContent />
    </AdminListPageWrapper>
  );
};

const IntroductionsAdminContent = () => {
  const { adminQueryOptions } = useContext(AdminTableContext);
  const [outcomeModalIsOpen, setOutcomeModalIsOpen] = useState<boolean>(false);
  const [outcomeModalIntroduction, setOutcomeModalIntroduction] = useState<AdminIntroduction>();

  const {
    data: adminIntroductions = [],
    isError,
    isLoading,
    isSuccess,
  } = AdminIntroductions.useGetIntroductions(adminQueryOptions, {
    query: {
      placeholderData: keepPreviousData,
    },
  });

  const { data: adminIntroductionsCount = 0 } = AdminIntroductions.useGetAdminIntroductionCount({
    filter: adminQueryOptions.filter,
  });

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor('introductionId', {
          cell: (introductionId) => introductionId.getValue(),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>ID</span>,
          maxSize: 10,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'text',
          },
          enableSorting: true,
        }),
        columnHelper.accessor('sentByUserType', {
          cell: (sentByUserType) => <div>{sentByUserType.getValue()}</div>,
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Sent By</span>,
          size: 25,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'text',
          },
          enableSorting: false,
        }),
        columnHelper.accessor('experteerName', {
          cell: ({ row }) => (
            <FlexibleLink
              type='legacy'
              href={`/professionals/${row.original.experteerId}`}
              className='text-brand-100 hover:underline'
            >
              {row.original.experteerName}
            </FlexibleLink>
          ),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Experteer Name</span>,
          size: 25,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'text',
          },
          enableSorting: false,
        }),
        columnHelper.accessor('representativeName', {
          cell: ({ row }) => (
            <FlexibleLink
              type='legacy'
              href={`/organization/${row.original.organizationId}/representative/${row.original.representativeId}`}
              className='text-brand-100 hover:underline'
            >
              {row.original.representativeName}
            </FlexibleLink>
          ),
          header: () => (
            <span className='overflow-ellipsis whitespace-nowrap'>Representative Name</span>
          ),
          size: 25,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'text',
          },
          enableSorting: false,
        }),
        columnHelper.accessor('organizationName', {
          cell: ({ row }) => (
            <FlexibleLink
              type='legacy'
              href={`/organization/${row.original.organizationId}`}
              className='text-brand-100 hover:underline'
            >
              {row.original.organizationName}
            </FlexibleLink>
          ),
          header: () => (
            <span className='overflow-ellipsis whitespace-nowrap'>Organization Name</span>
          ),
          size: 125,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'text',
          },
          enableSorting: true,
        }),
        columnHelper.accessor('projectName', {
          cell: ({ row }) => (
            <FlexibleLink
              type='legacy'
              href={`/admin/browse/project/${row.original.projectId}`}
              className='text-brand-100 hover:underline'
            >
              {row.original.projectName}
            </FlexibleLink>
          ),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Project Name</span>,
          size: 150,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'text',
          },
          enableSorting: false,
        }),
        columnHelper.accessor('outcome', {
          cell: ({ row, cell }) => {
            const stage = cell.getValue();
            let badgeColor = 'error';
            if (stage === 'accepted') {
              badgeColor = 'success-500';
            } else if (stage === 'pending') {
              badgeColor = 'gray';
            }
            return (
              <div className='flex justify-between align-middle'>
                <PillBadge variant='dot' size='md' color={badgeColor as PillBadgeColors}>
                  {stage}
                </PillBadge>
                <button
                  onClick={() => {
                    setOutcomeModalIntroduction(row.original);
                    setOutcomeModalIsOpen(true);
                  }}
                >
                  <Icon type='edit-01' size='14' />
                </button>
              </div>
            );
          },
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Outcome</span>,
          size: 25,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'text',
          },
          enableSorting: true,
        }),
        columnHelper.accessor('reason', {
          cell: (reason) => reason.getValue(),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Reason</span>,
          size: 25,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'text',
          },
          enableSorting: false,
        }),
        columnHelper.accessor('createdAt', {
          cell: (createdAt) => createdAt.getValue(),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Created At</span>,
          size: 25,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'date-select',
          },
          enableSorting: true,
        }),
        columnHelper.accessor('updatedAt', {
          cell: (updatedAt) => updatedAt.getValue(),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Updated At</span>,
          size: 25,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'date-select',
          },
          enableSorting: true,
        }),
      ] as ColumnDef<AdminIntroduction>[],
    [],
  );

  return (
    <>
      <AdminTable
        columns={columns}
        data={adminIntroductions}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        filteredCount={adminIntroductionsCount}
        enableFiltering={true}
      />
      <IntroductionOutcomeModal
        isOpen={outcomeModalIsOpen}
        onClose={() => setOutcomeModalIsOpen(false)}
        introduction={outcomeModalIntroduction as AdminIntroduction}
        page={adminQueryOptions.page}
        limit={adminQueryOptions.limit}
      />
    </>
  );
};
